import { useCallback, useState } from "react";
import {
  crearEmpresa,
  actualizarEmpresa,
  eliminarEmpresa,
} from "../../../../services/empresasService";
import {
  normalizeEmpresa,
  buildEmpresaPayload,
} from "../adapters/empresasAdapter";
import { initialEmpresaForm } from "../constants/empresas";
import { showToast } from "../../../../utils/showToast";

const validar = (form) => {
  if (!form.nombre.trim()) return "Ingresa el nombre de la empresa.";
  return null;
};

export default function useEmpresaForm({ onCreated, onUpdated, onDeleted }) {
  const [modal, setModal] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formNuevo, setFormNuevo] = useState(initialEmpresaForm);
  const [formEditar, setFormEditar] = useState(initialEmpresaForm);
  const [empresaEditando, setEmpresaEditando] = useState(null);

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialEmpresaForm);
    setModal("crear");
  }, []);

  const abrirEditar = useCallback((empresa) => {
    if (!empresa) return;
    const parsed = normalizeEmpresa(empresa);
    setEmpresaEditando(parsed);
    setFormEditar({
      nombre: parsed.nombre || "",
      ruc: parsed.ruc || "",
      telefono: parsed.telefono || "",
      email: parsed.email || "",
      tipo: parsed.tipo || "externa",
      activo: parsed.activo,
    });
    setModal("editar");
  }, []);

  const abrirEliminar = useCallback((empresa) => {
    if (!empresa) return;
    setEmpresaEditando(normalizeEmpresa(empresa));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setEmpresaEditando(null);
    setFormNuevo(initialEmpresaForm);
    setFormEditar(initialEmpresaForm);
  }, []);

  const handleCrear = useCallback(async () => {
    const validation = validar(formNuevo);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payload = buildEmpresaPayload(formNuevo);
      const creada = await crearEmpresa(payload);
      const normalized = normalizeEmpresa(creada);
      onCreated?.(normalized);
      showToast({ type: "success", message: "Empresa creada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo crear la empresa." });
    } finally {
      setSaving(false);
    }
  }, [formNuevo, onCreated, cerrarModal]);

  const handleActualizar = useCallback(async () => {
    if (!empresaEditando) return;
    const validation = validar(formEditar);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payload = buildEmpresaPayload(formEditar);
      const actualizada = await actualizarEmpresa(empresaEditando.id, payload);
      const normalized = normalizeEmpresa({ ...empresaEditando, ...actualizada });
      onUpdated?.(normalized);
      showToast({ type: "success", message: "Empresa actualizada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo actualizar la empresa." });
    } finally {
      setSaving(false);
    }
  }, [empresaEditando, formEditar, onUpdated, cerrarModal]);

  const handleEliminar = useCallback(async () => {
    if (!empresaEditando) return;
    setDeleting(true);
    try {
      await eliminarEmpresa(empresaEditando.id);
      onDeleted?.(empresaEditando);
      showToast({ type: "success", message: "Empresa eliminada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar la empresa." });
    } finally {
      setDeleting(false);
    }
  }, [empresaEditando, onDeleted, cerrarModal]);

  return {
    modal,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    empresaEditando,
    saving,
    deleting,
    abrirCrear,
    abrirEditar,
    abrirEliminar,
    cerrarModal,
    handleCrear,
    handleActualizar,
    handleEliminar,
  };
}
